import {
  Icon,
  SettingsGroup,
  SettingsGroupButtonItem,
  Tooltip,
} from '@sb/design-system';

/** Everything this panel needs to offer a cloud backup, owned above the
 *  modal so an in-flight capture survives the modal closing. */
export interface RestoreBackupOffer {
  /** The box can take cloud backups at all: the feature is flagged on. */
  isAvailable: boolean;
  isCloudConnected: boolean;
  isCheckingConnection: boolean;
  isRunning: boolean;
  /** A backup finished, cleanly, since this restore was delivered. */
  hasSucceeded: boolean;
  onStart: () => void;
}

interface RestoreBackupFirstPanelProps {
  backup: RestoreBackupOffer;
  disabled?: boolean;
}

/**
 * The way back, offered before the point of no return.
 *
 * A restore erases the robot's current database and files, and the operator
 * is the only one who knows whether anything on it since the backup was
 * taken still matters. Settings has a "Back Up to Cloud" button for exactly
 * this, but this modal blocks the whole screen — so it is offered here, or
 * it is not reachable at all until after the decision has been made.
 */
export function RestoreBackupFirstPanel({
  backup,
  disabled,
}: RestoreBackupFirstPanelProps) {
  if (!backup.isAvailable) {
    return null;
  }

  const label = () => {
    if (backup.isRunning) return 'Backing Up to Cloud…';
    if (backup.hasSucceeded) return 'Back Up to Cloud Again';

    return 'Back Up to Cloud';
  };

  return (
    <section
      className="tw-flex tw-flex-col"
      data-testid="restore-confirmation-backup-region"
    >
      <h5 className="tw-heading-40 tw-pl-16">Back up this robot first</h5>

      <p className="tw-text-13 tw-text-label-secondary tw-px-16 tw-pb-8">
        {backup.hasSucceeded
          ? 'This robot’s current state is saved to the cloud. You can restore back to it from Marvin if this one turns out to be the wrong backup.'
          : 'The restore erases what is on this robot now. If anything on it still matters, take a backup before you confirm — afterwards there is nothing left to take one from.'}
      </p>

      <SettingsGroup>
        <SettingsGroupButtonItem
          data-testid="restore-confirmation-backup-button"
          disabled={disabled || backup.isRunning || !backup.isCloudConnected}
          onClick={backup.onStart}
        >
          <Icon
            kind={backup.hasSucceeded ? 'checkmarkCircleFill' : 'cloud'}
            className={
              backup.hasSucceeded ? 'tw-icon-22 tw-text-green' : 'tw-icon-22'
            }
          />

          {/* The tooltip wraps the label rather than the row: a wrapper
              around the row would take the group's divider in place of the
              row, losing its inset. */}
          <Tooltip
            className="tw-flex-1"
            content={
              backup.isCheckingConnection
                ? 'Checking this robot’s connection to Standard Bots…'
                : 'This robot is offline. A cloud backup needs a connection to Standard Bots.'
            }
            isOpen={backup.isCloudConnected ? false : null}
          >
            <span>{label()}</span>
          </Tooltip>
        </SettingsGroupButtonItem>
      </SettingsGroup>
    </section>
  );
}
